import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, ArrowRight, Upload, FileText, Loader2, Check, X, 
  Sparkles, Eye, EyeOff, ChevronDown, ChevronUp, Trash2, 
  AlertCircle, CheckCircle2, FileWarning, Settings2, Layers,
  PanelRightOpen, PanelRightClose, ZoomIn, ZoomOut, Maximize2
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const STEPS = [
  { id: 1, title: 'Upload PDF', description: 'Select your PDF form' },
  { id: 2, title: 'AI Analysis', description: 'Extracting and organizing fields' },
  { id: 3, title: 'Review & Map', description: 'Adjust fields and mappings' },
  { id: 4, title: 'Finalize', description: 'Name and save template' }
];

const PdfTemplateBuilder = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  
  // File state
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  
  // Extraction state
  const [rawFields, setRawFields] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  
  // Review state
  const [sections, setSections] = useState([]);
  const [fields, setFields] = useState([]);
  const [expandedField, setExpandedField] = useState(null);
  
  // PDF Preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [previewZoom, setPreviewZoom] = useState(100);
  
  // Template info
  const [templateInfo, setTemplateInfo] = useState({
    name: '',
    description: '',
    category: '',
    display_mode: 'long_form'
  });

  // Create PDF preview URL when file changes
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPdfPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfPreviewUrl(null);
    }
  }, [file]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Please select a PDF file');
        return;
      }
      setFile(selectedFile);
      setTemplateInfo(prev => ({
        ...prev,
        name: selectedFile.name.replace('.pdf', '')
      }));
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.toLowerCase().endsWith('.pdf')) {
      setFile(droppedFile);
      setTemplateInfo(prev => ({
        ...prev,
        name: droppedFile.name.replace('.pdf', '')
      }));
    } else {
      toast.error('Please drop a PDF file');
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Step 2: Analyze PDF with AI
  const analyzeWithAI = async () => {
    if (!file) return;
    
    setIsProcessing(true);
    setCurrentStep(2);
    
    try {
      // Step 2a: Initial extraction
      setProcessingStatus('Reading PDF structure...');
      await new Promise(r => setTimeout(r, 500));
      
      setProcessingStatus('Extracting form fields...');
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      
      // Call analyze endpoint (combines extraction + AI)
      setProcessingStatus('Analyzing with AI...');
      const response = await fetch(`${API}/templates/pdf/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Analysis failed');
      }
      
      const data = await response.json();
      
      setRawFields(data.raw_fields || []);
      setAnalysis(data.analysis);
      
      setProcessingStatus('Organizing fields into sections...');
      await new Promise(r => setTimeout(r, 500));
      
      // Process analysis results
      const analyzedSections = data.analysis?.sections || [{ title: 'Form Fields', description: '', order: 0 }];
      const analyzedFields = data.analysis?.fields || [];
      
      setSections(analyzedSections.map((s, idx) => ({
        ...s,
        section_id: `sec_temp_${idx}`
      })));
      
      // Merge AI analysis with raw field data
      const processedFields = analyzedFields.map((field, idx) => {
        const rawField = data.raw_fields.find(rf => rf.pdf_field_name === field.pdf_field_name);
        
        // Process options to ensure they have proper structure
        let options = field.options;
        if (options && options.length > 0) {
          options = options.map((opt, optIdx) => {
            if (typeof opt === 'string') {
              // Convert string option to object
              const exportVal = rawField?.export_values?.[optIdx] || rawField?.options?.[optIdx] || opt;
              return { label: opt, export_value: exportVal };
            }
            // Ensure export_value exists
            if (!opt.export_value && rawField?.export_values?.[optIdx]) {
              return { ...opt, export_value: rawField.export_values[optIdx] };
            }
            return opt;
          });
        } else if (rawField?.options?.length > 0) {
          // Use raw field options if AI didn't provide any
          options = rawField.options.map((opt, optIdx) => ({
            label: opt,
            export_value: rawField.export_values?.[optIdx] || opt
          }));
        } else if (rawField?.export_value && ['checkbox', 'radio'].includes(field.field_type || rawField?.field_type)) {
          // For checkboxes/radios, create a single option with export value
          options = [{ label: 'Yes', export_value: rawField.export_value }];
        }
        
        return {
          ...field,
          field_id: `fld_temp_${idx}`,
          raw_data: rawField,
          should_ignore: field.should_ignore || false,
          required: true,
          options: options || null,
          export_value: rawField?.export_value || field.export_value
        };
      });
      
      // Add any raw fields that weren't included in AI analysis
      const processedNames = new Set(processedFields.map(f => f.pdf_field_name));
      const missingFields = data.raw_fields
        .filter(rf => !processedNames.has(rf.pdf_field_name))
        .filter(rf => !['button', 'signature'].includes(rf.field_type))
        .map((rf, idx) => {
          let options = null;
          if (rf.options?.length > 0) {
            options = rf.options.map((opt, optIdx) => ({
              label: opt,
              export_value: rf.export_values?.[optIdx] || opt
            }));
          } else if (rf.export_value && ['checkbox', 'radio'].includes(rf.field_type)) {
            options = [{ label: 'Yes', export_value: rf.export_value }];
          }
          
          return {
            field_id: `fld_temp_${processedFields.length + idx}`,
            pdf_field_name: rf.pdf_field_name,
            label: rf.nearby_label || rf.pdf_field_name.split('_').pop() || rf.pdf_field_name,
            description: '',
            field_type: rf.field_type,
            section_index: 0,
            order: processedFields.length + idx,
            should_ignore: false,
            required: true,
            options: options,
            export_value: rf.export_value,
            raw_data: rf
          };
        });
      
      setFields([...processedFields, ...missingFields]);
      
      setProcessingStatus('Complete!');
      await new Promise(r => setTimeout(r, 500));
      
      setCurrentStep(3);
      
    } catch (error) {
      toast.error(error.message);
      setCurrentStep(1);
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  // Update field
  const updateField = (fieldId, updates) => {
    setFields(prev => prev.map(f => 
      f.field_id === fieldId ? { ...f, ...updates } : f
    ));
  };

  // Update field option
  const updateFieldOption = (fieldId, optionIndex, updates) => {
    setFields(prev => prev.map(f => {
      if (f.field_id !== fieldId) return f;
      const newOptions = [...(f.options || [])];
      newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
      return { ...f, options: newOptions };
    }));
  };

  // Update section
  const updateSection = (index, updates) => {
    setSections(prev => prev.map((s, idx) => 
      idx === index ? { ...s, ...updates } : s
    ));
  };

  // Create template
  const createTemplate = async () => {
    if (!templateInfo.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setIsProcessing(true);
    setProcessingStatus('Creating template...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const templateData = {
        name: templateInfo.name,
        description: templateInfo.description,
        category: templateInfo.category || null,
        display_mode: templateInfo.display_mode,
        sections: sections,
        fields: fields.filter(f => !f.should_ignore).map(f => ({
          pdf_field_name: f.pdf_field_name,
          field_name: f.field_name || f.pdf_field_name.replace(/[\[\]\.]/g, '_'),
          label: f.label,
          description: f.description,
          field_type: f.field_type,
          section_index: f.section_index || 0,
          order: f.order || 0,
          required: f.required,
          options: f.options,
          export_value: f.raw_data?.export_value
        }))
      };
      
      formData.append('template_data', JSON.stringify(templateData));
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/pdf/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create template');
      }
      
      const template = await response.json();
      toast.success('PDF template created successfully!');
      navigate(`/templates/${template.template_id}/edit`);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  const activeFields = fields.filter(f => !f.should_ignore);
  const ignoredFields = fields.filter(f => f.should_ignore);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/templates')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Templates
            </Button>
            <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-medium">
              PDF Template Builder
            </span>
          </div>
        </div>
      </nav>

      <div className={`mx-auto px-6 py-8 transition-all duration-300 ${currentStep === 3 && showPreview ? 'max-w-7xl' : 'max-w-5xl'}`}>
        {/* Progress Steps */}
        <div className="mb-10">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                    currentStep > step.id 
                      ? 'bg-green-500 text-white' 
                      : currentStep === step.id 
                        ? 'bg-violet-600 text-white ring-4 ring-violet-100' 
                        : 'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                  </div>
                  <div className="mt-2 text-center">
                    <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-slate-400 hidden sm:block">{step.description}</p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-full h-1 mx-4 rounded ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'
                  }`} style={{ minWidth: '60px' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Upload */}
        {currentStep === 1 && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Upload Your PDF Form</h1>
              <p className="text-slate-500">Select a PDF with fillable form fields</p>
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`p-12 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${
                file 
                  ? 'border-green-400 bg-green-50' 
                  : 'border-slate-300 bg-white hover:border-violet-400 hover:bg-violet-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-lg font-semibold text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600 mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="mt-4 text-sm text-slate-500 hover:text-red-500 flex items-center gap-1 mx-auto"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-violet-600" />
                  </div>
                  <p className="text-lg font-semibold text-slate-700">
                    Drop your PDF here or click to browse
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    PDF must contain fillable form fields
                  </p>
                </div>
              )}
            </div>

            {file && (
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={analyzeWithAI}
                  className="h-12 px-8 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Analyze with AI
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Processing */}
        {currentStep === 2 && (
          <div className="max-w-md mx-auto text-center py-16">
            <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Analyzing Your PDF</h2>
            <p className="text-slate-500 mb-8">{processingStatus}</p>
            
            <div className="space-y-3 text-left bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm text-slate-600">Reading PDF structure</span>
              </div>
              <div className="flex items-center gap-3">
                {processingStatus.includes('Extracting') || processingStatus.includes('Analyzing') || processingStatus.includes('Organizing') || processingStatus.includes('Complete') ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
                )}
                <span className="text-sm text-slate-600">Extracting form fields</span>
              </div>
              <div className="flex items-center gap-3">
                {processingStatus.includes('Analyzing') || processingStatus.includes('Organizing') || processingStatus.includes('Complete') ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
                )}
                <span className="text-sm text-slate-600">AI analysis & labeling</span>
              </div>
              <div className="flex items-center gap-3">
                {processingStatus.includes('Organizing') || processingStatus.includes('Complete') ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
                )}
                <span className="text-sm text-slate-600">Organizing into sections</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review & Map */}
        {currentStep === 3 && (
          <div className="flex gap-6">
            {/* Main content - Fields mapping */}
            <div className={`flex-1 transition-all duration-300 ${showPreview ? 'max-w-[60%]' : 'max-w-full'}`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Review & Map Fields</h1>
                  <p className="text-slate-500">Adjust labels, sections, and mappings as needed</p>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-100 text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    {activeFields.length} active
                  </span>
                  {ignoredFields.length > 0 && (
                    <span className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600">
                      <EyeOff className="w-4 h-4" />
                      {ignoredFields.length} ignored
                    </span>
                  )}
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-violet-600 transition-colors"
                    title={showPreview ? 'Hide PDF Preview' : 'Show PDF Preview'}
                  >
                    {showPreview ? <PanelRightClose className="w-5 h-5" /> : <PanelRightOpen className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Sections */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Sections ({sections.length})
                </h3>
                <p className="text-xs text-slate-400 mb-4 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg">
                  💡 You can reorganize and reorder fields into different sections in the Template Editor after creation.
                </p>
              </div>

              {/* Fields grouped by Section */}
              <div className="space-y-6">
                {sections.map((section, sectionIdx) => {
                  const sectionFields = fields.filter(f => f.section_index === sectionIdx && !f.should_ignore);
                  const ignoredInSection = fields.filter(f => f.section_index === sectionIdx && f.should_ignore);
                  
                  return (
                    <div key={section.section_id} className="border border-slate-200 rounded-xl overflow-hidden">
                      {/* Section Header */}
                      <div className="bg-gradient-to-r from-violet-50 to-slate-50 px-4 py-3 border-b border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 mr-4">
                            <Input
                              value={section.title}
                              onChange={(e) => updateSection(sectionIdx, { title: e.target.value })}
                              className="h-8 font-semibold bg-transparent border-0 focus:bg-white focus:border px-0 text-slate-800"
                              placeholder="Section title"
                            />
                          </div>
                          <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded">
                            {sectionFields.length} field{sectionFields.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <Input
                          value={section.description || ''}
                          onChange={(e) => updateSection(sectionIdx, { description: e.target.value })}
                          className="h-7 text-sm bg-transparent border-0 focus:bg-white focus:border px-0 text-slate-500 mt-1"
                          placeholder="Section description (optional)"
                        />
                      </div>
                      
                      {/* Section Fields */}
                      <div className="divide-y divide-slate-100">
                        {sectionFields.length === 0 ? (
                          <div className="p-4 text-center text-slate-400 text-sm">
                            No active fields in this section
                          </div>
                        ) : (
                          sectionFields.map((field) => (
                            <div
                              key={field.field_id}
                              className="bg-white hover:bg-slate-50 transition-colors"
                            >
                              {/* Field Header */}
                              <div 
                                className="p-3 flex items-center gap-3 cursor-pointer"
                                onClick={() => setExpandedField(expandedField === field.field_id ? null : field.field_id)}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900 text-sm">{field.label}</span>
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">
                                      {field.field_type}
                                    </span>
                                    {field.matched_confidence && field.matched_confidence !== 'high' && (
                                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 text-xs">
                                        {field.matched_confidence}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 font-mono truncate mt-0.5">
                                    → {field.pdf_field_name || 'needs mapping'}
                                  </p>
                                </div>
                                
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); updateField(field.field_id, { should_ignore: true }); }}
                                    className="p-1.5 rounded hover:bg-orange-50 text-slate-400 hover:text-orange-600"
                                    title="Ignore field"
                                  >
                                    <EyeOff className="w-4 h-4" />
                                  </button>
                                  {expandedField === field.field_id ? (
                                    <ChevronUp className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  )}
                                </div>
                              </div>

                              {/* Expanded Field Details */}
                              {expandedField === field.field_id && (
                                <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3 bg-slate-50">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs text-slate-500 mb-1 block">Display Label</Label>
                                      <Input
                                        value={field.label}
                                        onChange={(e) => updateField(field.field_id, { label: e.target.value })}
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-slate-500 mb-1 block">Field Type</Label>
                                      <select
                                        value={field.field_type}
                                        onChange={(e) => updateField(field.field_id, { field_type: e.target.value })}
                                        className="w-full h-8 rounded-lg border border-slate-200 px-2 text-sm"
                                      >
                                        <option value="text">Text</option>
                                        <option value="textarea">Text Area</option>
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="date">Date</option>
                                        <option value="number">Number</option>
                                        <option value="dropdown">Dropdown</option>
                                        <option value="checkbox">Checkbox</option>
                                        <option value="radio">Radio</option>
                                      </select>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">PDF Field Mapping</Label>
                                    <select
                                      value={field.pdf_field_name}
                                      onChange={(e) => updateField(field.field_id, { pdf_field_name: e.target.value })}
                                      className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs font-mono"
                                    >
                                      <option value="">-- Select PDF field --</option>
                                      {rawFields.map(rf => (
                                        <option key={rf.pdf_field_name} value={rf.pdf_field_name}>
                                          {rf.pdf_field_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Help Text</Label>
                                    <Input
                                      value={field.description || ''}
                                      onChange={(e) => updateField(field.field_id, { description: e.target.value })}
                                      placeholder="Instructions for this field..."
                                      className="h-8 text-sm"
                                    />
                                  </div>

                                  {/* Options for multi-choice fields */}
                                  {field.options && field.options.length > 0 && (
                                    <div>
                                      <Label className="text-xs text-slate-500 mb-2 block">Options ({field.options.length})</Label>
                                      <div className="space-y-1.5 p-2 rounded-lg bg-white border border-slate-200 max-h-40 overflow-y-auto">
                                        <div className="grid grid-cols-2 gap-2 text-xs font-medium text-slate-400 px-1">
                                          <span>Display Label</span>
                                          <span>Export Value</span>
                                        </div>
                                        {field.options.map((opt, optIdx) => (
                                          <div key={optIdx} className="grid grid-cols-2 gap-2">
                                            <Input
                                              value={opt.label || ''}
                                              onChange={(e) => updateFieldOption(field.field_id, optIdx, { label: e.target.value })}
                                              className="h-7 text-xs"
                                              placeholder="Label"
                                            />
                                            <Input
                                              value={opt.export_value || ''}
                                              className="h-7 text-xs font-mono bg-slate-50"
                                              disabled
                                              title="Export value (read-only)"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                        
                        {/* Ignored fields in this section */}
                        {ignoredInSection.length > 0 && (
                          <div className="p-2 bg-slate-50">
                            <button
                              onClick={() => {/* Could expand to show ignored */}}
                              className="text-xs text-slate-400 hover:text-slate-600 w-full text-left px-2"
                            >
                              + {ignoredInSection.length} ignored field{ignoredInSection.length !== 1 ? 's' : ''} in this section
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Unassigned/Orphan fields */}
                {fields.filter(f => f.section_index === undefined || f.section_index >= sections.length).length > 0 && (
                  <div className="border border-orange-200 rounded-xl overflow-hidden">
                    <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
                      <h4 className="font-semibold text-orange-800">Unassigned Fields</h4>
                      <p className="text-xs text-orange-600">These fields need to be assigned to a section</p>
                    </div>
                    <div className="divide-y divide-orange-100">
                      {fields.filter(f => f.section_index === undefined || f.section_index >= sections.length).map(field => (
                        <div key={field.field_id} className="p-3 bg-white">
                          <span className="font-medium text-sm">{field.label}</span>
                          <span className="ml-2 text-xs text-slate-500">({field.field_type})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            {/* Actions */}
            <div className="mt-8 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="h-11 px-6 rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              <Button
                onClick={() => setCurrentStep(4)}
                className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            </div>

            {/* PDF Preview Panel */}
            {showPreview && pdfPreviewUrl && (
              <div className="w-[40%] flex-shrink-0">
                <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                  {/* Preview Header */}
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-slate-700">PDF Preview</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPreviewZoom(Math.max(50, previewZoom - 25))}
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        title="Zoom Out"
                        disabled={previewZoom <= 50}
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-slate-500 w-12 text-center">{previewZoom}%</span>
                      <button
                        onClick={() => setPreviewZoom(Math.min(200, previewZoom + 25))}
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        title="Zoom In"
                        disabled={previewZoom >= 200}
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      <a
                        href={pdfPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors ml-1"
                        title="Open in New Tab"
                      >
                        <Maximize2 className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                  
                  {/* PDF Viewer */}
                  <div className="h-[calc(100vh-220px)] overflow-hidden bg-slate-100 relative">
                    {/* Embed PDF - will work in browsers with PDF plugin */}
                    <div 
                      className="absolute inset-0 flex justify-center overflow-auto"
                      style={{ transform: `scale(${previewZoom / 100})`, transformOrigin: 'top center' }}
                    >
                      <embed
                        src={pdfPreviewUrl}
                        type="application/pdf"
                        className="w-full h-full bg-white"
                        style={{ minHeight: '100%' }}
                      />
                    </div>
                    
                    {/* Fallback overlay - shown when PDF doesn't render (e.g., no plugin) */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100/95 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-xs pointer-events-auto">
                        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-violet-600" />
                        </div>
                        <h4 className="font-semibold text-slate-900 mb-2">{file?.name}</h4>
                        <p className="text-sm text-slate-500 mb-4">
                          {file ? `${(file.size / 1024).toFixed(1)} KB` : 'PDF Document'}
                        </p>
                        <a
                          href={pdfPreviewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors text-sm font-medium"
                        >
                          <Maximize2 className="w-4 h-4" />
                          Open PDF in New Tab
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview Footer */}
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
                    <p className="text-xs text-slate-500 text-center">
                      Use preview to identify fields while mapping
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Finalize */}
        {currentStep === 4 && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Finalize Template</h1>
              <p className="text-slate-500">Name your template and save</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Template Name *</Label>
                <Input
                  value={templateInfo.name}
                  onChange={(e) => setTemplateInfo(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter template name"
                  className="h-11 rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</Label>
                <Textarea
                  value={templateInfo.description}
                  onChange={(e) => setTemplateInfo(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this template for?"
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Display Mode</Label>
                <select
                  value={templateInfo.display_mode}
                  onChange={(e) => setTemplateInfo(prev => ({ ...prev, display_mode: e.target.value }))}
                  className="w-full h-11 rounded-xl border border-slate-200 px-4"
                >
                  <option value="long_form">Long Form (all sections visible)</option>
                  <option value="wizard">Wizard (step-by-step sections)</option>
                </select>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-xl bg-violet-50 border border-violet-100">
                <h4 className="font-medium text-violet-900 mb-2">Template Summary</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-violet-600">Fields:</span>
                    <span className="ml-2 font-medium text-violet-900">{activeFields.length}</span>
                  </div>
                  <div>
                    <span className="text-violet-600">Sections:</span>
                    <span className="ml-2 font-medium text-violet-900">{sections.length}</span>
                  </div>
                  <div>
                    <span className="text-violet-600">Ignored:</span>
                    <span className="ml-2 font-medium text-violet-900">{ignoredFields.length}</span>
                  </div>
                  <div>
                    <span className="text-violet-600">Type:</span>
                    <span className="ml-2 font-medium text-violet-900">PDF Form</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(3)}
                className="h-11 px-6 rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={createTemplate}
                disabled={isProcessing || !templateInfo.name.trim()}
                className="h-11 px-8 rounded-xl bg-green-600 hover:bg-green-700 text-white"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Create Template
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfTemplateBuilder;
